import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import _ from "lodash";
import Input from "../../forms/Input";
import Button from "../../Button";
import Products from "./products/Products";
import { parse } from "date-fns/esm";

const INVOICE_DEFAULTS = {
    id: "",
    reference: "",
    amount_excl_vat: "",
    amount_incl_vat: "",
    invoice_value_percentage: 100,
    vat_percentage: 20,
    payment_due_at: "",
    payment_due_at_display: "",
    sent_at: "",
    sent_at_display: "",
};

const INPUT_TYPES = [
    {
        label: "Percentage",
        value: "percentage",
    },
    {
        label: "Value",
        value: "value",
    },
];

const roundFloatToTwoDecimalPoints = (value) => {
    return (
        ((value * 100) / 100)
            .toFixed(2)
    );
};

const calculateVatAmount = (amount, vatPercentage) => {
    return (parseFloat(amount) / 100) * parseFloat(vatPercentage);
};

const addVatToAmount = (amount, vatPercentage) => {
    
    return parseFloat(amount) +
              calculateVatAmount(amount, vatPercentage);
};

const CreateEditInvoice = (props) => {
    const {
        jobId = null,
        orderId = null,
        invoiceId = null,
        onCancel = () => {},
        onComplete = () => {},
    } = props;

    const prepareDefaultValues = () => {
        const defaultInvoice = INVOICE_DEFAULTS;

        if (!!invoiceId && invoiceId !== "") {
            defaultInvoice.id = parseInt(invoiceId);
        }

        return defaultInvoice;
    };

    const [inputType, setInputType] = useState(INPUT_TYPES[0].value);
    const [percentageChanged, setPercentageChanged] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [job, setJob] = useState(null);
    const [order, setOrder] = useState(null);
    const [products, setProducts] = useState([]);
    const [invoice, setInvoice] = useState(prepareDefaultValues());
    const [errors, setErrors] = useState(null);

    useEffect(() => {
        load();
    }, [jobId, orderId, invoiceId]);

    const load = () => {
        axios
            .get(
                route("web-api.job.order.invoice.config", {
                    job: jobId,
                    order: orderId,
                    invoice_id: invoiceId,
                }).toString()
            )
            .then((response) => {
                if (!!response && !!response.data) {
                    if (!!response.data.job) {
                        setJob(response.data.job);
                    }

                    if (!!response.data.order) {
                        setOrder(response.data.order);
                    }

                    if (!!response.data.products) {
                        setProducts(response.data.products);
                    }

                    if (!!response.data.invoice) {
                        setInvoice(response.data.invoice);
                    } else {
                        let defaultInvoice = _.cloneDeep(INVOICE_DEFAULTS);

                        if (!!response.data.order) {
                            defaultInvoice.amount_excl_vat =
                                roundFloatToTwoDecimalPoints(
                                    response.data.order.total_amount_remainder
                                );
                            defaultInvoice.amount_incl_vat =
                                roundFloatToTwoDecimalPoints(
                                    addVatToAmount(
                                        defaultInvoice.amount_excl_vat,
                                        defaultInvoice.vat_percentage
                                    )
                                );
                        }

                        setInvoice(defaultInvoice);
                    }
                }
            })
            .catch((error) => {
                console.log(error);
                toast.error(<div>{"An error occurred"}</div>);
                setSubmitting(false);
            });
    };

    useEffect(() => {
        if (!!submitting) {
            submit();
        }
    }, [submitting]);

    const reset = () => {
        setInvoice(prepareDefaultValues());

        setSubmitting(false);
        setErrors(null);
    };

    const payload = () => {
        return {
            method: !!invoiceId ? "patch" : "post",
            url: !!invoiceId
                ? route("web-api.job.order.invoice.update", {
                      job: jobId,
                      order: orderId,
                      invoice: invoiceId,
                  }).toString()
                : route("web-api.job.order.invoice.store", {
                      job: jobId,
                      order: orderId,
                  }).toString(),
            data: {
                ...invoice,
            },
        };
    };

    const submit = () => {
        const data = payload();
        axios(data)
            .then((response) => {
                reset();
                onComplete(response.data.invoice_id);
            })
            .catch((error) => {
                console.log(error);
                if (!!error.response.data && !!error.response.data.errors) {
                    setErrors(error.response.data.errors);
                    toast.error(
                        <div>
                            {!!error.response.data.message
                                ? error.response.data.message
                                : "An error occurred"}
                        </div>
                    );
                }

                setSubmitting(false);
            });
    };

    const handleClose = () => {
        if (!submitting) {
            reset();
            onCancel();
        }
    };

    const onChange = (field, value) => {
        const clone = _.cloneDeep(invoice);

        switch (field) {
            case "invoice_value_percentage":
                /*
                 * Only update when the appropriate input type is selected
                 * This is to prevent a feedback loop
                 */
                if (inputType === INPUT_TYPES[0].value) {
                    clone[field] = value;
                    setPercentageChanged(true);
                }
                break;
            case "amount_excl_vat":
                /*
                 * Only update when the appropriate input type is selected
                 * This is to prevent a feedback loop
                 */
                if (inputType === INPUT_TYPES[1].value) {
                    clone[field] = value;
                }
                break;
            case "vat_percentage":
                clone[field] = value;
                clone.amount_incl_vat = roundFloatToTwoDecimalPoints(
                    addVatToAmount(
                        clone.amount_excl_vat,
                        !!value ? parseFloat(value) : 0
                    )
                );
                break;
            default:
                clone[field] = value;
                break;
        }

        setInvoice(clone);
    };

    const handleInvoiceValueRecalculation = () => {
        const clone = _.cloneDeep(invoice);

        const remainingAmount = parseFloat(
            !!order.total_amount_remainder ? order.total_amount_remainder : 0
        );

        if (inputType === INPUT_TYPES[0].value) {
            /*
             * Process Values Using The Percentage Input
             */
            let percentageToBePaid = parseFloat(
                !!clone.invoice_value_percentage
                    ? clone.invoice_value_percentage
                    : 0
            );
            let finalAmount = remainingAmount;

            if (percentageToBePaid <= 99) {
                finalAmount = (remainingAmount / 100) * percentageToBePaid;
            }

            clone.amount_excl_vat = finalAmount.toFixed(2);
            clone.amount_incl_vat = roundFloatToTwoDecimalPoints(
                addVatToAmount(
                    clone.amount_excl_vat,
                    !!clone.vat_percentage ? clone.vat_percentage : 0
                )
            );
        } else if (inputType === INPUT_TYPES[1].value) {
            /*
             * Process Values Using The Value Input
             */
            clone.amount_incl_vat = roundFloatToTwoDecimalPoints(
                addVatToAmount(
                    clone.amount_excl_vat,
                    !!clone.vat_percentage ? clone.vat_percentage : 0
                )
            );

            // Calculate The Percentage
            clone.invoice_value_percentage = (
                (clone.amount_excl_vat / remainingAmount) *
                100
            ).toFixed(2);
        }

        setInvoice(clone);
        setPercentageChanged(false);
    };

    return (
        <React.Fragment>
            <div className="row m-0 mb-4 p-0">
                <div className="col-12 col-lg-6 m-0 p-0 pr-lg-2 bespoke-input">
                    <Input
                        name={"reference"}
                        label={"Invoice Reference"}
                        required={true}
                        type={"number"}
                        value={invoice.reference}
                        onChange={(value) => onChange("reference", value)}
                        error={
                            !!errors && !!errors.reference
                                ? errors.reference
                                : null
                        }
                    />
                </div>
            </div>
            <div className="row m-0 mb-4 p-0">
                <div className="col-12 m-0 p-0">
                    <Products products={products} order={order} />
                </div>
            </div>
            <div className={"m-0 mb-4"}>
                <div className="d-flex justify-content-between align-items-center border-bottom pb-2 mb-3">
                    <div className={"h5 mb-0 text-eh-navy"}>Pricing</div>
                </div>
                <div className="row m-0 p-0 border-bottom mb-3">
                    <div className="col-12 col-md-6 m-0 p-0 pr-md-2 bespoke-input">
                        <Input
                            name={"input_type"}
                            label={"Input Type"}
                            type={"select"}
                            options={INPUT_TYPES}
                            value={inputType}
                            disabled={submitting}
                            onChange={(value) => setInputType(value)}
                        />
                    </div>
                    <div className="col-12 col-md-6 m-0 p-0 pl-md-2 bespoke-input">
                        <Input
                            name={"total_amount_remainder"}
                            label={"Total Remaining (£ Excl .VAT)"}
                            required={false}
                            type={"number"}
                            value={
                                !!order && !!order.total_amount_remainder
                                    ? order.total_amount_remainder
                                    : 0
                            }
                            disabled={true}
                            onChange={(value) => {
                                /* No Manual Changing */
                            }}
                            step={0.01}
                        />
                    </div>
                </div>
                <div className="m-0 p-0 border-bottom mb-3">
                    <div className="row m-0 p-0">
                        <div
                            className={
                                inputType === INPUT_TYPES[1].value
                                    ? "col-12 m-0 p-0"
                                    : "col-6 m-0 p-0 pr-2"
                            }
                        >
                            <Input
                                name={"invoice_value_percentage"}
                                label={"Invoice Value (%)"}
                                required={true}
                                type={"number"}
                                value={invoice.invoice_value_percentage}
                                disabled={
                                    inputType === INPUT_TYPES[1].value ||
                                    submitting
                                }
                                onChange={(value) =>
                                    onChange("invoice_value_percentage", value)
                                }
                                error={
                                    !!errors &&
                                    !!errors.invoice_value_percentage
                                        ? errors.invoice_value_percentage
                                        : null
                                }
                                min={0}
                                max={100}
                                step={0.01}
                            />
                        </div>
                        {inputType === INPUT_TYPES[0].value && (
                            <div className="col-6 m-0 p-0 pl-2 d-flex align-items-end">
                                <Button
                                    onClick={() =>
                                        handleInvoiceValueRecalculation()
                                    }
                                    disabled={submitting}
                                    label={"Recalculate Values"}
                                    classes={"btn btn-eh-lilac m-0 mr-0 mb-3"}
                                    style={{
                                        width: "100%",
                                        height: "calc(1.6em + 1rem + 2px)",
                                        minHeight: "initial",
                                        justifyContent: "center",
                                        textAlign: "center",
                                        fontSize: "12px",
                                    }}
                                />
                            </div>
                        )}
                    </div>
                    <div className="row m-0 p-0">
                        <div
                            className={
                                inputType === INPUT_TYPES[0].value
                                    ? "col-12 m-0 p-0"
                                    : "col-6 m-0 p-0 pr-2"
                            }
                        >
                            <Input
                                name={"amount_excl_vat"}
                                label={"Invoice Value (£ Excl .VAT)"}
                                required={true}
                                type={"number"}
                                value={invoice.amount_excl_vat}
                                disabled={
                                    inputType === INPUT_TYPES[0].value ||
                                    submitting
                                }
                                onChange={(value) =>
                                    onChange("amount_excl_vat", value)
                                }
                                step={0.01}
                                min={0}
                                max={
                                    !!order && !!order.total_amount_remainder
                                        ? order.total_amount_remainder
                                        : 0
                                }
                            />
                        </div>
                        {inputType === INPUT_TYPES[1].value && (
                            <div className="col-6 m-0 p-0 pl-2 d-flex align-items-end">
                                <Button
                                    onClick={() =>
                                        handleInvoiceValueRecalculation()
                                    }
                                    disabled={submitting}
                                    label={"Recalculate Values"}
                                    classes={"btn btn-eh-lilac m-0 mr-0 mb-3"}
                                    style={{
                                        width: "100%",
                                        height: "calc(1.6em + 1rem + 2px)",
                                        minHeight: "initial",
                                        justifyContent: "center",
                                        textAlign: "center",
                                        fontSize: "12px",
                                    }}
                                />
                            </div>
                        )}
                    </div>
                </div>
                <div className="m-0 p-0 border-bottom mb-3">
                    <div className="row m-0 p-0">
                        <div className={"col-6 m-0 p-0 pr-2"}>
                            <Input
                                name={"vat_percentage"}
                                label={"VAT Amount (%)"}
                                required={true}
                                type={"number"}
                                value={invoice.vat_percentage}
                                disabled={submitting}
                                onChange={(value) =>
                                    onChange("vat_percentage", value)
                                }
                                error={
                                    !!errors && !!errors.vat_percentage
                                        ? errors.vat_percentage
                                        : null
                                }
                                min={0}
                                max={100}
                                step={0.01}
                            />
                        </div>
                        <div className={"col-6 m-0 p-0 pl-2"}>
                            <Input
                                name={"vat_amount"}
                                label={"VAT Amount (£)"}
                                required={true}
                                type={"number"}
                                value={roundFloatToTwoDecimalPoints(
                                    calculateVatAmount(
                                        invoice.amount_excl_vat,
                                        invoice.vat_percentage
                                    )
                                )}
                                disabled={true}
                                onChange={(value) => {
                                    /* No Manual Changing */
                                }}
                                min={0}
                                step={0.01}
                            />
                        </div>
                    </div>
                    <div className="row m-0 p-0">
                        <div className="col-12 m-0 p-0">
                            <Input
                                name={"amount_incl_vat"}
                                label={"Invoice Value (£ Incl .VAT)"}
                                required={true}
                                type={"number"}
                                value={invoice.amount_incl_vat}
                                disabled={true}
                                onChange={(value) => {
                                    /* No Manual Changing */
                                }}
                                step={0.01}
                            />
                        </div>
                    </div>
                </div>
            </div>
            <div className={"m-0 mb-4 border-bottom pb-3"}>
                <div className="d-flex justify-content-between align-items-center border-bottom pb-2 mb-3">
                    <div className={"h5 mb-0 text-eh-navy"}>Date</div>
                </div>
                <div className="row m-0 p-0">
                    <div className="col-12 col-lg-6 m-0 p-0 pr-lg-2 bespoke-input">
                        <Input
                            name={"payment_due_at"}
                            label={"Payment Due At"}
                            required={false}
                            type={"date"}
                            value={invoice.payment_due_at}
                            disabled={submitting}
                            onChange={(value) =>
                                onChange("payment_due_at", value)
                            }
                            error={
                                !!errors && !!errors.payment_due_at
                                    ? errors.payment_due_at
                                    : null
                            }
                        />
                    </div>
                    <div className="col-12 col-lg-6 m-0 p-0 pl-lg-2 bespoke-input">
                        <Input
                            name={"sent_at"}
                            label={"Sent At"}
                            required={false}
                            type={"date"}
                            value={invoice.sent_at}
                            disabled={submitting}
                            onChange={(value) => onChange("sent_at", value)}
                            error={
                                !!errors && !!errors.sent_at
                                    ? errors.sent_at
                                    : null
                            }
                        />
                    </div>
                </div>
            </div>
            {percentageChanged === true && (
                <div className="row m-0 mb-3 p-0">
                    <div className="col-12 m-0 p-0">
                        <p className={"m-0 text-eh-navy"}>
                            <small>
                                Invoice Value (%) has changed, press
                                "Recalculate Invoice Value" to recalculate.
                            </small>
                        </p>
                    </div>
                </div>
            )}
            <div className="row m-0 p-0">
                <div className="col-12 m-0 p-0 d-flex justify-content-between align-items-center">
                    <div className="d-flex justify-content-start align-items-center">
                        <Button
                            onClick={() => setSubmitting(true)}
                            label={
                                !!invoiceId ? "Save Changes" : "Create Invoice"
                            }
                            classes={"btn btn-eh-lilac m-0 mr-4"}
                            disabled={percentageChanged || submitting}
                        />
                        <Button
                            onClick={() => handleClose()}
                            label={"Cancel"}
                            classes={"btn btn-outline-eh-off-black m-0 mr-4"}
                        />
                    </div>
                </div>
            </div>
        </React.Fragment>
    );
};

export default CreateEditInvoice;
